import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import Icon from '../shared/Icon';

const LAYER_IDS = ['imagem', 'linhas', 'falhas', 'relatorio'];
const LAYER_IMG = {
  imagem: null,
  linhas: '/assets/linhas.webp',
  falhas: '/assets/falhas.webp',
  relatorio: '/assets/relatorio.webp',
};
const LAYER_METRIC_V = {
  imagem: '0,5 cm',
  linhas: '262 km',
  falhas: '48 km',
  relatorio: 'PDF+',
};

export default function Hero() {
  const { t } = useTranslation();
  const [active, setActive] = useState('falhas');
  const [autoCycle, setAutoCycle] = useState(true);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!autoCycle) return;
    const timer = setInterval(() => {
      setActive((prev) => {
        const idx = LAYER_IDS.indexOf(prev);
        return LAYER_IDS[(idx + 1) % LAYER_IDS.length];
      });
    }, 3800);
    return () => clearInterval(timer);
  }, [autoCycle]);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !rootRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(rootRef.current.querySelectorAll('[data-hero-reveal]'), {
        opacity: 0, y: 22, duration: 0.85, ease: 'power2.out', stagger: 0.08, delay: 0.15,
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const activeDesc = t(`home.hero.layers.${active}.desc`);

  return (
    <section
      ref={rootRef}
      style={{
        position: 'relative',
        minHeight: '100vh',
        paddingTop: 64,
        overflow: 'hidden',
        background: 'var(--p2a-dark-bg)',
      }}
    >
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url("/assets/pixviewer_gaps_overview.webp")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transform: 'scale(1.05)',
        filter: 'brightness(0.55) saturate(0.9) contrast(1.05)',
      }} />
      {LAYER_IDS.filter((id) => LAYER_IMG[id]).map((id) => (
        <div
          key={id}
          style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url("${LAYER_IMG[id]}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: active === id ? 0.95 : 0,
            transition: 'opacity 600ms var(--p2a-ease)',
            mixBlendMode: 'screen',
          }}
        />
      ))}
      <div style={{
        position: 'absolute', inset: 0,
        background:
          'linear-gradient(180deg, rgba(5,7,10,0.80) 0%, rgba(5,7,10,0.25) 28%, rgba(5,7,10,0.35) 60%, rgba(5,7,10,0.92) 100%)',
      }} />
      <div className="grid-bg" style={{ position: 'absolute', inset: 0, opacity: 0.5 }} />

      <div
        className="p2-container"
        style={{
          position: 'relative', zIndex: 2,
          paddingTop: 56, paddingBottom: 64,
          minHeight: 'calc(100vh - 64px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          gap: 64,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: 48, alignItems: 'end' }} className="hero-grid">
          <div>
            <div className="eyebrow" data-hero-reveal style={{ marginBottom: 28 }}>
              <span className="dot" />{t('home.hero.eyebrow')}
            </div>
            <h1 className="h-display" data-hero-reveal>
              {t('home.hero.title_line1')} <br />
              <span style={{ color: 'var(--p2a-green-400)' }}>{t('home.hero.title_line2_highlight')}</span>
            </h1>
            <p data-hero-reveal style={{ marginTop: 28, fontSize: 18, lineHeight: 1.55, maxWidth: 540, color: 'var(--p2a-dark-fg-2)' }}>
              {t('home.hero.lede_pre')}{' '}
              <strong style={{ color: 'var(--p2a-dark-fg-1)', fontWeight: 600 }}>{t('home.hero.lede_strong')}</strong>{' '}
              {t('home.hero.lede_post')}
            </p>
            <div data-hero-reveal style={{ display: 'flex', gap: 12, marginTop: 32, flexWrap: 'wrap' }}>
              <a
                href="https://api.whatsapp.com/send?phone=5519996542014&text=Ol%C3%A1%2C%20gostaria%20de%20conhecer%20mais%20sobre%20as%20solu%C3%A7%C3%B5es%20que%20a%20Pix2Agro%20oferece."
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                {t('home.hero.cta_primary')} <Icon.ArrowRight />
              </a>
              <a href="#tour" className="btn btn-ghost">
                {t('home.hero.cta_secondary')} <Icon.ArrowDown />
              </a>
            </div>
          </div>

          <div
            className="panel hero-panel"
            data-hero-reveal
            style={{
              padding: 0,
              background: 'rgba(11,15,20,0.72)',
              backdropFilter: 'blur(14px) saturate(130%)',
              WebkitBackdropFilter: 'blur(14px) saturate(130%)',
              border: '1px solid var(--p2a-dark-border-2)',
              alignSelf: 'stretch',
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px',
              borderBottom: '1px solid var(--p2a-dark-border)',
            }}>
              <span className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--p2a-dark-fg-3)' }}>
                {t('home.hero.panel_title')}
              </span>
              <button
                onClick={() => setAutoCycle((a) => !a)}
                className="mono"
                style={{
                  fontSize: 10, letterSpacing: '0.14em',
                  color: autoCycle ? 'var(--p2a-green-400)' : 'var(--p2a-dark-fg-4)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: autoCycle ? 'var(--p2a-green-500)' : 'var(--p2a-dark-fg-4)',
                  boxShadow: autoCycle ? '0 0 0 4px rgba(34,197,94,0.2)' : 'none',
                }} />
                {autoCycle ? t('home.hero.panel_auto') : t('home.hero.panel_paused')}
              </button>
            </div>

            <div>
              {LAYER_IDS.map((id, i) => (
                <button
                  key={id}
                  onClick={() => { setActive(id); setAutoCycle(false); }}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '16px 18px',
                    textAlign: 'left',
                    background: active === id ? 'rgba(34,197,94,0.08)' : 'transparent',
                    borderBottom: i < LAYER_IDS.length - 1 ? '1px solid var(--p2a-dark-border)' : 'none',
                    borderLeft: `2px solid ${active === id ? 'var(--p2a-green-500)' : 'transparent'}`,
                    transition: 'background var(--p2a-dur-base) var(--p2a-ease), border-color var(--p2a-dur-base) var(--p2a-ease)',
                    cursor: 'pointer',
                  }}
                >
                  <span className="mono" style={{ fontSize: 10, color: 'var(--p2a-dark-fg-4)', width: 28, letterSpacing: '0.08em' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: active === id ? 'var(--p2a-green-500)' : 'var(--p2a-slate-600)',
                    boxShadow: active === id ? '0 0 0 4px rgba(34,197,94,0.2)' : 'none',
                    transition: 'all var(--p2a-dur-base) var(--p2a-ease)',
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 500,
                      color: active === id ? 'var(--p2a-dark-fg-1)' : 'var(--p2a-dark-fg-2)',
                    }}>{t(`home.hero.layers.${id}.label`)}</div>
                    <div style={{ fontSize: 12, color: 'var(--p2a-dark-fg-4)', marginTop: 2 }}>{t(`home.hero.layers.${id}.sub`)}</div>
                  </div>
                  <span className="mono" style={{
                    fontSize: 11,
                    color: active === id ? 'var(--p2a-green-400)' : 'var(--p2a-dark-fg-4)',
                    letterSpacing: '0.06em',
                  }}>
                    {LAYER_METRIC_V[id]}
                  </span>
                </button>
              ))}
            </div>

            <div style={{
              padding: '14px 18px',
              borderTop: '1px solid var(--p2a-dark-border)',
              fontSize: 12.5,
              lineHeight: 1.5,
              color: 'var(--p2a-dark-fg-3)',
              minHeight: 52,
            }}>
              {activeDesc}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .hero-panel { max-width: 520px; }
        }
      `}</style>
    </section>
  );
}
